import { useMemo, useEffect } from "react"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useBackgroundRefresh } from "@hydrologiq/shared/monitoring/hooks/useBackgroundRefresh"
import {
  GetAssetMonitoringGraphsDocument,
  type GetAssetMonitoringGraphsQuery,
  type GetAssetMonitoringGraphsQueryVariables,
} from "@hydrologiq/gql-experience/graphql"
import { getSeriesColor } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import type {
  TimeSeriesDataPoint,
  TimeSeriesSeries,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"

/** Overlay configuration (mirrors AssetOverlayConfig from GraphQL). */
export interface OverlayConfig {
  graphId: string
  label: string
  yAxisLabel: string
  sameAxis: boolean
  defaultEnabled: boolean
}

/** Mapped overlay data ready for the TimeSeriesGraph component. */
export interface OverlayPayload {
  graphId: string
  field: string
  unit: string | null
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<string[]>[]
}

/** A single server-resolved monitoring graph mapped to the recharts shape. */
export interface MonitoringGraphPayload {
  graphId: string
  title: string
  unit: string | null
  capacity: number | null
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<string[]>[]
  availableOverlays: OverlayConfig[] | null
  overlays: OverlayPayload[] | null
}

export type ServerGraph = GetAssetMonitoringGraphsQuery["getAssetMonitoringGraphs"][number]

/**
 * Map a server-resolved graph directly to the recharts shape.
 * The server already handles grouping, unit scaling (W→kW), and peak inclusion —
 * the client passes values through unchanged.
 */
export function mapServerGraph(graph: ServerGraph): MonitoringGraphPayload {
  // Build a unified timestamp set from all series points
  const timestampSet = new Set<string>()
  for (const s of graph.series) {
    for (const point of s.points) {
      timestampSet.add(point.time)
    }
  }
  const sortedTimestamps = Array.from(timestampSet).sort()

  // Build data points: one row per timestamp, one key per series
  const data: TimeSeriesDataPoint[] = sortedTimestamps.map((timestamp) => {
    const point: TimeSeriesDataPoint = { timestamp }
    for (const s of graph.series) {
      const match = s.points.find((p) => p.time === timestamp)
      point[s.key] = match?.value ?? null
    }
    return point
  })

  // Build series descriptors for recharts
  const series: TimeSeriesSeries<string[]>[] = graph.series.map((s, index) => ({
    name: s.key,
    color: getSeriesColor(index, graph.series.length),
    label: s.label,
    strokeWidth: 2,
    connectNulls: false,
    capacity: s.capacity ?? null,
  }))

  // Map overlay configurations
  const serverOverlayConfigs = (graph as Record<string, unknown>).availableOverlays as
    | Array<{ graphId: string; label: string; yAxisLabel: string; sameAxis: boolean; defaultEnabled: boolean }>
    | null
    | undefined
  const availableOverlays: OverlayConfig[] | null = serverOverlayConfigs?.length
    ? serverOverlayConfigs.map((o) => ({
        graphId: o.graphId,
        label: o.label,
        yAxisLabel: o.yAxisLabel,
        sameAxis: o.sameAxis,
        defaultEnabled: o.defaultEnabled,
      }))
    : null

  // Map overlay data
  const serverOverlays = (graph as Record<string, unknown>).overlays as
    | Array<{ graphId: string; field: string; unit?: string | null; series: ServerGraph["series"] }>
    | null
    | undefined
  const overlays: OverlayPayload[] | null = serverOverlays?.length
    ? serverOverlays.map((overlay) => {
        const overlayTimestampSet = new Set<string>()
        for (const s of overlay.series) {
          for (const point of s.points) {
            overlayTimestampSet.add(point.time)
          }
        }
        const overlayTimestamps = Array.from(overlayTimestampSet).sort()

        const overlayData: TimeSeriesDataPoint[] = overlayTimestamps.map((timestamp) => {
          const point: TimeSeriesDataPoint = { timestamp }
          for (const s of overlay.series) {
            const match = s.points.find((p) => p.time === timestamp)
            point[s.key] = match?.value ?? null
          }
          return point
        })

        const overlaySeries: TimeSeriesSeries<string[]>[] = overlay.series.map((s, index) => ({
          name: s.key,
          color: getSeriesColor(index, overlay.series.length),
          label: s.label,
          strokeWidth: 2,
          connectNulls: false,
          capacity: (s as Record<string, unknown>).capacity as number | null ?? null,
        }))

        return {
          graphId: overlay.graphId,
          field: overlay.field,
          unit: overlay.unit ?? null,
          data: overlayData,
          series: overlaySeries,
        }
      })
    : null

  return {
    graphId: graph.graphId,
    title: graph.title,
    unit: graph.unit ?? null,
    capacity: graph.capacity ?? null,
    data,
    series,
    availableOverlays,
    overlays,
  }
}

export interface UseAssetMonitoringGraphsParams {
  assetId: string
  start: string
  stop: string
  granularity?: string
  refreshKey?: number
}

const DEFAULT_GRANULARITY = "5m"

export interface UseAssetMonitoringGraphsResult {
  graphs: MonitoringGraphPayload[]
  loading: boolean
  error?: Error
  refetch: () => void
}

/**
 * Fetches server-resolved monitoring graphs for a single asset.
 * The server owns which graphs an asset shows — no client-supplied graph batch needed.
 */
export const useAssetMonitoringGraphs = (
  params: UseAssetMonitoringGraphsParams
): UseAssetMonitoringGraphsResult => {
  const { assetId, start, stop, granularity = DEFAULT_GRANULARITY, refreshKey } = params

  const { beginFetch, isBackground } = useBackgroundRefresh()

  const [executeQuery, { data, previousData, loading, error }] = useLazyQueryWithEndpoint<
    GetAssetMonitoringGraphsQuery,
    GetAssetMonitoringGraphsQueryVariables
  >(
    GetAssetMonitoringGraphsDocument,
    {
      notifyOnNetworkStatusChange: true,
    },
    "primary"
  )

  useEffect(() => {
    beginFetch(refreshKey)
    executeQuery({
      variables: {
        assetId,
        start,
        stop,
        granularity,
      },
    })
  }, [assetId, start, stop, granularity, refreshKey, executeQuery, beginFetch])

  // An automatic refresh keeps the previous graphs on screen and reports no loading until the new data arrives.
  const background = isBackground()
  const current = data ?? (background && !error ? previousData : undefined)

  const graphs = useMemo(() => {
    const serverGraphs = current?.getAssetMonitoringGraphs
    if (!serverGraphs?.length) {
      return []
    }
    return serverGraphs.map(mapServerGraph)
  }, [current])

  /** 
   * NOTE: Only use refetch on query FAILURE but 
   * NOT in cases where variable state may have 
   * changed as this state change will not persist 
   * in the query
  **/ 
  const refetch = () => {
    executeQuery({
      variables: {
        assetId,
        start,
        stop,
        granularity,
      },
    })
  }

  return {
    graphs,
    loading: loading && !background,
    error: error as Error | undefined,
    refetch,
  }
}
